import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Sprout } from 'lucide-react';
import nurseryService from '../../services/nursery';
import nurseryPropagationService from '../../services/nurseryPropagation';
import LotPlaceFilter from '../../components/nursery/LotPlaceFilter';
import { slotWhere, stockItemLabel } from '../../utils/nurseryRowSlots';
import type {
  NurseryLocation, NurseryStockItem, LotStage, CreateLotPayload, PlaceFilter,
} from '../../types/nursery';
import type { ContainerType } from '../../types/nurseryPropagation';
import { MANUAL_LOT_STAGES, LOT_STAGE_LABELS } from '../../types/nursery';

/** Names only what the farm / block / row choices above have not already said. */
function locationLabel(l: NurseryLocation, place: PlaceFilter): string {
  if (l.location_type === 'nursery_row' && l.row_number != null) {
    if (place.row_number) return `Position ${l.sequence_number}`;
    const where = slotWhere({ ...l, block_name: place.block_id ? null : l.block_name });
    return place.farm_id || !l.farm_name ? where : `${l.farm_name} · ${where}`;
  }
  return `${l.name} (${l.location_type.replace('_', ' ')})`;
}

function inPlace(l: NurseryLocation, place: PlaceFilter): boolean {
  if (place.farm_id && l.farm_id !== place.farm_id) return false;
  if (place.block_id && l.block_id !== place.block_id) return false;
  if (place.row_number) {
    if (l.row_number !== place.row_number) return false;
    if ((l.row_side ?? 'none') !== (place.row_side ?? 'none')) return false;
  }
  return true;
}

function LotCreate() {
  const navigate = useNavigate();
  // /nursery/lots/new?location_id=N arrives with the place already chosen.
  const [searchParams] = useSearchParams();
  const presetLocationId = Number(searchParams.get('location_id')) || null;
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  const [stockItemsLoaded, setStockItemsLoaded] = useState(false);
  const [stockItemId, setStockItemId] = useState<number | ''>('');
  const [place, setPlace] = useState<PlaceFilter>({});
  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [containers, setContainers] = useState<ContainerType[]>([]);

  const [form, setForm] = useState<CreateLotPayload>({
    cultivar_id: 0, tree_type_id: 0, current_stage: 'sown', initial_count: 0, record_origin: true,
    ...(presetLocationId ? { location_id: presetLocationId } : {}),
  });
  // Typed as text so "6.45" is not rounded or reformatted while typing.
  const [costText, setCostText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getStockItems({ active: true }).then(r => {
      if (r.success) setStockItems(r.data || []);
      setStockItemsLoaded(true);
    });
    nurseryPropagationService.getContainerTypes().then(r => {
      if (r.success) setContainers(r.data || []);
    }).catch(() => { /* no trays to offer; the lot is placed without them */ });
    nurseryService.getLocations({ active: true }).then(r => {
      if (!r.success) return;
      setLocations(r.data || []);
      const preset = (r.data || []).find(l => l.id === presetLocationId);
      if (preset) {
        setPlace({
          farm_id: preset.farm_id ?? undefined,
          block_id: preset.block_id ?? undefined,
          ...(preset.row_number ? { row_number: preset.row_number, row_side: preset.row_side ?? 'none' } : {}),
        });
      }
    });
  }, [presetLocationId]);

  const placeLocations = useMemo(() => locations.filter(l => inPlace(l, place)), [locations, place]);

  // Only a stock item can be placed: the order form sells stock items, so a
  // lot of anything else is trees nobody can order. A plant lot records the
  // item's cultivar, rootstock and tree type; merchandise names only the item,
  // and is ready stock from the moment it is placed (retail plan Phase 0b).
  const chooseStockItem = (id: number | '') => {
    setStockItemId(id);
    const item = stockItems.find(i => i.id === id);
    setForm(f => ({ ...f, cultivar_id: item?.cultivar_id ?? 0, rootstock_id: item?.rootstock_id ?? null, tree_type_id: item?.tree_type_id ?? 0 }));
  };
  const merch = stockItems.find(i => i.id === stockItemId)?.kind === 'merchandise';

  // Narrowing the place drops a chosen location that is no longer inside it.
  const choosePlace = (next: PlaceFilter) => {
    setPlace(next);
    setForm(f => {
      const chosen = locations.find(l => l.id === f.location_id);
      return chosen && !inPlace(chosen, next) ? { ...f, location_id: null } : f;
    });
  };

  const set = (patch: Partial<CreateLotPayload>) => setForm(f => ({ ...f, ...patch }));
  const container = containers.find(c => c.id === form.container_type_id);

  const submit = async () => {
    if (!stockItemId) { setError('Choose a stock item'); return; }
    const cost = costText.trim() === '' ? null : Number(costText.replace(/[$,\s]/g, ''));
    if (cost != null && (!Number.isFinite(cost) || cost < 0)) { setError('Enter the cost as a number, such as 6.45'); return; }
    setSubmitting(true);
    setError(null);
    try {
      // No location chosen: the farm or block picked above still says where
      // the trees are, and the server puts them in its "Row not set" place.
      const base: CreateLotPayload = merch
        ? {
          stock_item_id: Number(stockItemId), location_id: form.location_id ?? null,
          initial_count: form.initial_count ?? 0, received_on: form.received_on ?? null,
          source_supplier: form.source_supplier ?? null, record_origin: true,
          ...(cost != null ? { unit_cost: cost } : {}),
        }
        : { ...form, ...(cost != null ? { unit_cost: cost } : {}) };
      const payload: CreateLotPayload = form.location_id ? base : {
        ...base,
        ...(place.block_id ? { block_id: place.block_id } : place.farm_id ? { farm_id: place.farm_id } : {}),
      };
      const res = await nurseryService.createLot(payload);
      if (res.success && res.data) navigate(`/nursery/lots/${res.data.id}`);
      else setError((res as { error?: { message?: string } }).error?.message || 'Failed to create lot');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader title="Place a Lot" subtitle="Put stock into a location" icon={<Sprout className="h-6 w-6 text-white" />} />
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-5">
          <div>
            <label htmlFor="lot-stock-item" className="block text-sm font-medium text-neutral-700 mb-1">Stock item <span className="text-red-500">*</span></label>
            <select id="lot-stock-item" value={stockItemId}
              onChange={e => chooseStockItem(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Choose a stock item…</option>
              {stockItems.map(i => <option key={i.id} value={i.id}>{stockItemLabel(i)}</option>)}
            </select>
            <p className="text-xs text-neutral-400 mt-1 break-words">
              {stockItemsLoaded && stockItems.length === 0 ? 'No stock items yet. ' : 'Only stock items can be placed. Growing something new? '}
              <Link to="/nursery/stock-items/new" className="text-primary-700 hover:underline">Add a stock item</Link>
            </p>
          </div>

          {merch && (
            <p className="text-xs text-neutral-500">Merchandise is ready to sell as soon as it is placed. Put it on the shop floor or in a storeroom.</p>
          )}

          {!merch && (<>
          <div>
            <label htmlFor="lot-stage" className="block text-sm font-medium text-neutral-700 mb-1">Stage</label>
            <select id="lot-stage" value={form.current_stage} onChange={e => set({ current_stage: e.target.value as LotStage })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              {MANUAL_LOT_STAGES.filter(s => !(form.is_mother && s === 'ready'))
                .map(s => <option key={s} value={s}>{LOT_STAGE_LABELS[s]}</option>)}
            </select>
          </div>

          <div>
            <label className="inline-flex items-center gap-2 text-sm font-medium text-neutral-700">
              <input type="checkbox" checked={!!form.is_mother} className="rounded border-neutral-300"
                onChange={e => set({
                  is_mother: e.target.checked,
                  ...(e.target.checked && form.current_stage === 'ready' ? { current_stage: 'growing' as LotStage } : {}),
                })} />
              Mother plant
            </label>
            <p className="text-xs text-neutral-400 mt-1">
              A stool bed or stock plants. Layers, cuttings and wood are taken from it without reducing its count, and it is never ready stock.
            </p>
          </div>

          {containers.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="lot-container" className="block text-sm font-medium text-neutral-700 mb-1">In</label>
                <select id="lot-container" value={form.container_type_id ?? ''}
                  onChange={e => set({ container_type_id: e.target.value ? Number(e.target.value) : null })}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">No trays</option>
                  {containers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {container && (
                <div>
                  <label htmlFor="lot-container-count" className="block text-sm font-medium text-neutral-700 mb-1">How many</label>
                  <input id="lot-container-count" type="text" inputMode="numeric" value={form.container_count ?? ''}
                    onChange={e => {
                      const n = e.target.value === '' ? null : parseInt(e.target.value, 10);
                      if (n != null && Number.isNaN(n)) return;
                      // Trays × plants per tray is the count, until one is typed.
                      set({
                        container_count: n,
                        ...(n != null && !form.initial_count ? { initial_count: n * container.cells_per_container } : {}),
                      });
                    }}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              )}
            </div>
          )}
          </>)}

          <div>
            <p className="block text-sm font-medium text-neutral-700 mb-1">Where</p>
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <LotPlaceFilter value={place} onChange={choosePlace} allLabel="Any" />
            </div>
            <label htmlFor="lot-location" className="block text-sm font-medium text-neutral-700 mb-1">Location</label>
            <select id="lot-location" value={form.location_id ?? ''} onChange={e => set({ location_id: e.target.value ? Number(e.target.value) : null })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
              <option value="">{place.block_id || place.farm_id ? 'Row and position not known' : 'Unassigned'}</option>
              {placeLocations.map(l => <option key={l.id} value={l.id}>{locationLabel(l, place)}</option>)}
            </select>
            <p className="text-xs text-neutral-400 mt-1">
              {placeLocations.length} location{placeLocations.length === 1 ? '' : 's'} to choose from. Pick a farm, block and row above to narrow them.
            </p>
            {!form.location_id && (
              <p className={place.block_id || place.farm_id ? 'text-xs text-neutral-500 mt-1' : 'text-xs text-amber-700 mt-1'}>
                {place.block_id || place.farm_id
                  ? 'The lot goes in “Row not set” on the farm or block chosen above. Pickers see it there.'
                  : 'No place is recorded. Pickers see the lot at whichever farm its load goes from.'}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Count</label>
              <input type="number" min="0" value={form.initial_count ?? 0}
                onChange={e => set({ initial_count: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            {!merch && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Plant / sow date</label>
              <input type="date" value={form.plant_date ?? ''} onChange={e => set({ plant_date: e.target.value || null })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Source / supplier (provenance)</label>
            <input value={form.source_supplier ?? ''} onChange={e => set({ source_supplier: e.target.value || null })}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" placeholder="e.g. seed from XYZ Seeds" />
          </div>

          {/* Bought in rather than grown: what one cost and when it arrived, for margin. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="lot-unit-cost" className="block text-sm font-medium text-neutral-700 mb-1">Cost each, ex GST (bought in)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-neutral-400">$</span>
                <input id="lot-unit-cost" type="text" inputMode="decimal" value={costText}
                  onChange={e => setCostText(e.target.value)}
                  placeholder="Grown here: leave blank"
                  className="w-full border border-neutral-300 rounded-lg pl-7 pr-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label htmlFor="lot-received-on" className="block text-sm font-medium text-neutral-700 mb-1">Received</label>
              <input id="lot-received-on" type="date" value={form.received_on ?? ''}
                onChange={e => set({ received_on: e.target.value || null })}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          <p className="text-xs text-neutral-400">
            The ready date is computed automatically from the tree type's readiness rule once dates are set.
          </p>

          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/lots')}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={submit} disabled={submitting}>
              {submitting ? 'Placing…' : 'Place lot'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default LotCreate;
